import React, { useCallback, useEffect, useRef, useState } from 'react';
import { shouldOfferBiometricUnlock } from '../permissions';
import { PermissionPrePrompt } from '../ui/screens';
import { ShellButton } from '../ui/ShellButton';
import { RubyGem } from '../ui/RubyGem';
import { PALETTE, FONT } from '../ui/tokens';
import type { ShellTheme } from '../ui/tokens';
import { KEYS, safeGet, safeSet, safeRemove } from '../storage';
import type { KeyValueStore } from '../storage';

/** P5 adapter: @capgo/capacitor-native-biometric. */
export interface BiometricPort {
  isAvailable(): Promise<boolean>;
  verify(reason: string): Promise<boolean>;
}

/** Re-lock after this long in the background (plan R7). */
export const LOCK_AFTER_MS = 5 * 60 * 1000;

export function shouldLock(o: { enabled: boolean; phase: 'launch' | 'resume'; backgroundedAt: number | null; now: number }): boolean {
  if (!o.enabled) return false;
  if (o.phase === 'launch') return true;
  return o.backgroundedAt !== null && o.now - o.backgroundedAt >= LOCK_AFTER_MS;
}

/**
 * Face ID / biometric app lock for a signed-in user (plan R7). Offered on the
 * second signed-in launch through our pre-prompt; once on, the app is hidden
 * at launch and after 5 minutes away until the user verifies.
 */
export function BiometricLock({ biometric, store, platform, theme, signedIn, onResume, onPause, onUsePassword, now, children }: {
  biometric: BiometricPort; store: KeyValueStore; platform: 'ios' | 'android'; theme: ShellTheme; signedIn: boolean;
  onResume: (cb: () => void) => () => void; onPause: (cb: () => void) => () => void; now: () => number; children: React.ReactNode;
  /** The way out if biometrics stop working: the app signs the user out so they use their password. */
  onUsePassword: () => void;
}) {
  const [locked, setLocked] = useState<boolean | null>(signedIn ? null : false);
  const [offer, setOffer] = useState(false);
  const enabledRef = useRef(false);
  const backgroundedAt = useRef<number | null>(null);
  const reason = platform === 'ios' ? 'Unlock RubyTill' : 'Unlock RubyTill';
  const label = platform === 'ios' ? 'Unlock with Face ID' : 'Unlock';

  const tryUnlock = useCallback(async () => {
    let ok = false;
    try { ok = await biometric.verify(reason); } catch { ok = false; }
    if (ok) setLocked(false);
  }, [biometric, reason]);

  useEffect(() => {
    if (!signedIn) { setLocked(false); return; }
    let cancelled = false;
    void (async () => {
      const enabled = (await safeGet(store, KEYS.bioEnabled)) === '1';
      const declined = (await safeGet(store, KEYS.bioDeclined)) === '1';
      const launches = (Number(await safeGet(store, KEYS.bioLaunches)) || 0) + 1;
      await safeSet(store, KEYS.bioLaunches, String(launches));
      enabledRef.current = enabled;
      if (cancelled) return;
      if (shouldLock({ enabled, phase: 'launch', backgroundedAt: null, now: now() })) { setLocked(true); void tryUnlock(); }
      else setLocked(false);
      if (!enabled && !declined) {
        let available = false;
        try { available = await biometric.isAvailable(); } catch { available = false; }
        if (!cancelled && shouldOfferBiometricUnlock({ passwordSignIns: launches, biometrics: available ? 'prompt' : 'unavailable', enabled, declined })) setOffer(true);
      }
    })();
    return () => { cancelled = true; };
  }, [signedIn, store, biometric, now, tryUnlock]);

  useEffect(() => {
    if (!signedIn) return;
    const offP = onPause(() => { backgroundedAt.current = now(); });
    const offR = onResume(() => {
      if (shouldLock({ enabled: enabledRef.current, phase: 'resume', backgroundedAt: backgroundedAt.current, now: now() })) { setLocked(true); void tryUnlock(); }
    });
    return () => { offP(); offR(); };
  }, [signedIn, onPause, onResume, now, tryUnlock]);

  if (locked === null) return <div style={{ position: 'fixed', inset: 0, background: PALETTE[theme].ground }} />;
  if (locked) {
    return (
      <div style={{ position: 'fixed', inset: 0, zIndex: 2147482500, background: PALETTE[theme].ground, color: PALETTE[theme].ink, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 20, padding: 24, font: `16px ${FONT}` }}>
        <RubyGem size={64} />
        <div style={{ width: '100%', maxWidth: 360, display: 'flex', flexDirection: 'column', gap: 10 }}>
          <ShellButton theme={theme} primary onClick={() => { void tryUnlock(); }}>{label}</ShellButton>
          <ShellButton theme={theme} onClick={() => {
            enabledRef.current = false;
            void safeRemove(store, KEYS.bioEnabled).then(() => { setLocked(false); onUsePassword(); });
          }}>Sign in with password</ShellButton>
        </div>
      </div>
    );
  }
  return (
    <>
      {children}
      {offer && (
        <PermissionPrePrompt
          permission="biometrics" platform={platform} theme={theme}
          onAllow={() => {
            setOffer(false);
            void (async () => {
              let ok = false;
              try { ok = await biometric.verify('Turn on Face ID for RubyTill'); } catch { ok = false; }
              if (ok) { enabledRef.current = true; await safeSet(store, KEYS.bioEnabled, '1'); }
            })();
          }}
          onNotNow={() => { setOffer(false); void safeSet(store, KEYS.bioDeclined, '1'); }}
        />
      )}
    </>
  );
}
